import { useCallback } from 'react'
import type {
  MutationHookContext,
  HookFetcherContext,
} from '@commerce/utils/types'
import { ValidationError } from '@commerce/utils/errors'
import useRemoveLine, { UseRemoveLine } from '@commerce/actual-cart/use-remove-line'
import type { Cart, RemoveItemHook } from '../types/cart'
import useCart from './use-cart'


/** The schema’s entry-point for mutations. This acts as the public, top-level API from which all mutation queries must start. */
export type MutationCartLinesRemoveArgs = {
  lineIds: Array<Scalars['ID']>
  cartId: Scalars['ID']
}


export type RemoveLineFn<T = any> = T extends any
  ? (input?: RemoveLineActionInput<T>) => Promise<Cart | null | undefined>
  : (input: RemoveLineActionInput<T>) => Promise<Cart | null>

export type RemoveLineActionInput<T = any> = T extends any
  ? Partial<RemoveItemHook['actionInput']>
  : RemoveItemHook['actionInput']

export default useRemoveLine as UseRemoveLine<typeof handler>

import {
  cartLinesRemoveMutation,
  getCartId,
  cartToCart,
} from '../utils'

import { Mutation, Scalars } from '../schema'

export const handler = {
  fetchOptions: {
    query: cartLinesRemoveMutation,
  },
  async fetcher({
    input: { itemId },
    options,
    fetch,
  }: HookFetcherContext<RemoveItemHook>) {
    const data = await fetch<Mutation, MutationCartLinesRemoveArgs>({
      ...options,
      variables: { cartId: getCartId(), lineIds: [itemId] },
    })
    return cartToCart((data as any).cartLinesRemove)
  },
  useHook: ({ fetch }: MutationHookContext<RemoveItemHook>) => <
    T extends { id: string, [key: string]: any } | undefined = undefined
  >(
    ctx: { item?: T } = {}
  ) => {
    const { item } = ctx
    const { mutate } = useCart()
    const removeLine: RemoveLineFn<any> = async (input) => {
      const itemId = input?.id ?? item?.id

      if (!itemId) {
        throw new ValidationError({
          message: 'Invalid input used for this operation',
        })
      }

      const data = await fetch({ input: { itemId } })
      // Update cache with new cart and revalidate so all subscribers (e.g. cart count in nav) re-render
      await mutate(data, true)
      return data
    }

    return useCallback(removeLine as RemoveLineFn<T>, [fetch, mutate])
  },
}
